import { workspaceByIdQueryDocument } from "@/domain/workspace/hooks/api/workspace.gql";
import { type CreateActiveViewMutation } from "@/graphql/generated/graphql";
import { useMutation } from "@apollo/client";

import { createActiveViewMutationDocument } from "./active-view.gql";
import { EVENT_ACTIVE_VIEW_CREATED, trackActiveViewEvent } from "./tracking";

export type CreatedActiveView = CreateActiveViewMutation["createActiveView"];

export interface UseCreateActiveViewProps {
  onCompleted?: (data: CreatedActiveView) => void;
  onError?: (e: Error) => void;
}

export const useCreateActiveView = ({ onCompleted, onError }: UseCreateActiveViewProps = {}) => {
  const [createActiveView, { loading, error }] = useMutation(createActiveViewMutationDocument, {
    onCompleted: onUpdateCompleted,
    onError: onUpdateError,
    update: (cache, { data }) => {
      const newActiveView = data?.createActiveView;
      if (!newActiveView) {
        return;
      }

      const cachedQueryResult = cache.readQuery({
        query: workspaceByIdQueryDocument,
        variables: { input: newActiveView.workspaceId },
      });

      if (!cachedQueryResult) {
        return;
      }

      cache.writeQuery({
        query: workspaceByIdQueryDocument,
        variables: { input: newActiveView.workspaceId },
        data: {
          workspaceById: {
            ...cachedQueryResult.workspaceById,
            activeViewList: [...cachedQueryResult.workspaceById.activeViewList, newActiveView],
          },
        },
      });

      trackActiveViewEvent(EVENT_ACTIVE_VIEW_CREATED, newActiveView);
    },
  });

  function onUpdateCompleted(data: CreateActiveViewMutation) {
    if (onCompleted) {
      onCompleted(data.createActiveView);
    }
  }

  function onUpdateError(e: Error) {
    if (onError) {
      onError(e);
    }
  }

  return {
    createActiveView,
    loading,
    error,
  };
};
